import {
  DataResult,
  process,
  SortDescriptor,
  State
} from '@progress/kendo-data-query';
import { Grid, GridProps } from '@progress/kendo-react-grid';
import { ForwardedRef, forwardRef, ReactNode, useMemo, useState } from 'react';
import { LoadingPanel } from './LoadingPanel';

type DataGridProps<T> = {
  items?: T[];
  loading?: boolean;
  children: ReactNode;
  pageSize?: number;
  initialSort?: SortDescriptor[];
  onPageChange?: (skip: number, take: number) => void;
} & Omit<GridProps, 'onPageChange'>;

function DataGridInner<T>(props: DataGridProps<T>, ref: ForwardedRef<Grid>) {
  const {
    items = [],
    data,
    onRowClick,
    onSortChange,
    onFilterChange,
    onPageChange,
    children,
    loading,
    sortable = true,
    filterable = true,
    pageable = false,
    pageSize = 20,
    initialSort = [],
    scrollable = 'scrollable',
    ...rest
  } = props;

  const [dataState, setDataState] = useState<State>({
    skip: pageable ? 0 : undefined,
    take: pageable ? pageSize : undefined,
    sort: initialSort,
    filter: {
      logic: 'or',
      filters: []
    }
  });

  const onDataStateChange = (dataState: State) => {
    setDataState(dataState);
  };

  const dataResult = useMemo(() => {
    return data
      ? process((data as DataResult).data, dataState)
      : process(items, dataState);
  }, [items, data, dataState]);

  return (
    <>
      <Grid
        ref={ref}
        style={{
          maxHeight: '75vh'
        }}
        data={data ? data : dataResult}
        skip={dataState.skip}
        take={dataState.take}
        sort={dataState.sort}
        filter={dataState.filter}
        sortable={sortable}
        pageable={pageable}
        filterable={filterable}
        scrollable={scrollable}
        onSortChange={event => {
          onDataStateChange({
            ...dataState,
            sort: event.sort
          });
          onSortChange && onSortChange(event);
        }}
        onPageChange={({ page }) => {
          onDataStateChange({
            ...dataState,
            skip: page.skip,
            take: page.take
          });
          onPageChange && onPageChange(page.skip, page.take);
        }}
        onFilterChange={event => {
          onDataStateChange({
            ...dataState,
            filter: event.filter,
            skip: 0
          });
          onFilterChange && onFilterChange(event);
        }}
        onRowClick={onRowClick}
        {...rest}
      >
        {children}
      </Grid>
      {loading && <LoadingPanel />}
    </>
  );
}

export const DataGrid = forwardRef(DataGridInner) as <T>(
  props: DataGridProps<T> & { ref?: React.ForwardedRef<Grid> }
) => ReturnType<typeof DataGridInner>;
